import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { HandoffForm } from '@/Domain/components/Action/HandoffAction/HandoffAction.sheet';
import { IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';
import LockLineIcon from '@/Domain/components/icons/LockLineIcon';
import { TrashIcon } from '@/Domain/components/icons/Trash.icon';
import { AppHelper } from '@/Domain/helper/App.helper';

type Props = {
  form: UseFormReturn<HandoffForm>;
};

const MAX_GUIDELINES = 10;
export const HandoffRulesField = ({ form }: Props) => {
  const { t } = useTranslation('handoff_action');

  const predefinedRules = useFieldArray({ name: 'generic_guidelines', control: form.control });
  const rules = useFieldArray({ name: 'custom_guidelines', control: form.control });

  const handleAddField = () =>
    rules.append({ id: null, name: '', description: '', is_active: false });
  const handelRemoveField = (index: number) => rules.remove(index);

  return (
    <div id="handoff_rules">
      <FormLabel>{t('form.handoff_rules_label')}</FormLabel>
      <FormDescription>{t('form.handoff_rules_description')}</FormDescription>

      <div className="mt-4">
        <section
          className="grid grid-cols-[minmax(100px,0.5fr)_minmax(100px,1fr)_50px_auto_40px]
gap-4 items-center py-3 px-4 text-foreground-subtle bg-muted border-b text-sm font-medium"
        >
          <span>{t('form.handoff_rules_table.rule_name')}</span>
          <span>{t('form.handoff_rules_table.description')}</span>
          <span>{t('form.handoff_rules_table.sate')}</span>
          <span />
          <Tooltip open={rules.fields.length >= MAX_GUIDELINES ? undefined : false}>
            <TooltipTrigger asChild>
              <div>
                <IconButton
                  type="button"
                  onClick={handleAddField}
                  disabled={rules.fields.length >= MAX_GUIDELINES}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </TooltipTrigger>
            <TooltipContent align="end">
              {t('tooltip.max_guidelines_reached', { max: MAX_GUIDELINES })}
            </TooltipContent>
          </Tooltip>
        </section>
        {predefinedRules.fields.map((arrayField, index) => (
          <div key={`predefined-rule-${arrayField.id}`}>
            <section
              className="grid grid-cols-[minmax(100px,0.5fr)_minmax(100px,1fr)_50px_auto_40px]
 items-center py-6 px-4 gap-4"
            >
              <FormField
                control={form.control}
                name={`generic_guidelines.${index}.name`}
                render={({ field }) => (
                  <p className="text-sm text-foreground">
                    {AppHelper.humanizeSnakeCase(field.value)}
                  </p>
                )}
              />

              <FormField
                control={form.control}
                name={`generic_guidelines.${index}.description`}
                render={({ field }) => <p className="text-sm text-foreground">{field.value}</p>}
              />

              <FormField
                control={form.control}
                name={`generic_guidelines.${index}.is_active`}
                render={({ field }) => (
                  <Switch
                    defaultChecked={field.value}
                    checked={field.value}
                    onCheckedChange={(check) => field.onChange(check)}
                  />
                )}
              />

              <Separator orientation="vertical" />

              <div className="mx-auto">
                <LockLineIcon />
              </div>
            </section>
            <Separator />
          </div>
        ))}

        {rules.fields.map((arrayField, index) => (
          <div key={`rule-${arrayField.id}`}>
            <section
              className="grid grid-cols-[minmax(100px,0.5fr)_minmax(100px,1fr)_50px_auto_40px]
 items-start py-6 pr-4 gap-4"
            >
              <FormField
                control={form.control}
                name={`custom_guidelines.${index}.name`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        size="sm"
                        placeholder={t('form.handoff_rules_table.name_placeholder')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`custom_guidelines.${index}.description`}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        {...field}
                        size="sm"
                        placeholder={t('form.handoff_rules_table.description_placeholder')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={`custom_guidelines.${index}.is_active`}
                render={({ field }) => (
                  <Switch
                    className="mt-2"
                    defaultChecked={field.value}
                    checked={field.value}
                    onCheckedChange={(check) => field.onChange(check)}
                  />
                )}
              />

              <Separator orientation="vertical" />

              <Tooltip>
                <TooltipTrigger asChild>
                  <IconButton
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={() => handelRemoveField(index)}
                    className="hover:!text-red-500"
                  >
                    <TrashIcon />
                  </IconButton>
                </TooltipTrigger>
                <TooltipContent>{t('tooltip.delete')}</TooltipContent>
              </Tooltip>
            </section>
            <Separator />
          </div>
        ))}
      </div>
    </div>
  );
};
